/**
 * AppV2Routes — the new /app/* subtree wired to the Go API.
 *
 * CRM/Leads (crm-m1-lld.md) is now Go-backed and mounted here — see
 * features/crm/api.ts. The legacy /leads, /leads/:id, /dashboard/leads, and
 * /sales/config routes in App.tsx now redirect here instead of rendering a
 * second copy of these pages: LeadsPage/LeadDetailsPage/LeadConfigPage/
 * LeadsDashboardPage were rewired in place to call apiV2 (Bearer token from
 * authV2Store), so a legacy-shell mount of the same components would have
 * no valid session to call the Go API with.
 */
import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthV2Store } from '@/features/auth/authV2Store';
import { webAccessDenial, type WebAccessDenial } from '@/features/auth/webAccess';
import { sessionPhase } from '@/features/auth/sessionPhase';
import { WebAccessDenied } from '@/features/auth/WebAccessDenied';
import { authApi } from '@/lib/apiV2';
import { ApiError } from '@/features/auth/types';
import { AppShellLegacy } from '@/features/_shell/AppShellLegacy';
import { PageLoader, SessionLoader } from '@/features/_shell/SessionLoader';
import { navigationItems } from '@/features/_shell/navConfig';
import { navItemVisible } from '@/features/_shell/navAccess';
import { useSelectedSchoolId } from '@/features/_shell/schoolSelection';
import { useRegisterWebDevice } from '@/features/notifications/useRegisterWebDevice';

// Lazy-load heavy pages.
const LoginV2Page = React.lazy(() => import('@/features/auth/LoginV2Page'));
const StudentsListPage = React.lazy(() => import('@/features/students/StudentsListPage'));
const TeachersPage = React.lazy(() => import('@/features/teachers/TeachersPage'));
const StudentDetailPage = React.lazy(() => import('@/features/students/detail/StudentDetailPage'));
// SIS admin console (gap #36, docs/design/sis-console-promotion-lld.md) —
// school-scoped roster + issuance (TC / ID card) + student-photo upload, and
// the year-end promotion wizard. Static sub-pages rank above nothing dynamic
// here; declared together for readability.
const SisRosterPage = React.lazy(() => import('@/features/students/RosterPage'));
const SisPromotionWizard = React.lazy(() => import('@/features/students/PromotionWizard'));
const SisPromotionHistoryPage = React.lazy(() => import('@/features/students/PromotionHistoryPage'));
const AcademicStructurePage = React.lazy(() => import('@/features/academic/AcademicStructurePage'));
// Org & School Settings console (docs/design/settings-console-lld.md) —
// org-scoped, Go-backed. Supersedes the retired legacy pages/settings/* and
// pages/system-user/* (Fastify-backed); the legacy /settings and /system-user
// routes in App.tsx now redirect here.
const SettingsConsolePage = React.lazy(() => import('@/features/settings/SettingsConsolePage'));
const TicketsPage = React.lazy(() => import('@/features/tickets/TicketsPage'));
const TicketDetailsPage = React.lazy(() => import('@/features/tickets/TicketDetailsPage'));
// Whole-school Task Management (Module 10, docs/design/tasks-module-ui-lld.md)
// — the SEPARATE org-scoped /tasks manager, NOT the CRM's lead-scoped
// crm_tasks (features/crm, mounted under leads/* below).
const TasksPage = React.lazy(() => import('@/features/tasks/TasksPage'));
const TaskDetailPage = React.lazy(() => import('@/features/tasks/TaskDetailPage'));
const PtmDashboardPage = React.lazy(() => import('@/features/ptm/PtmDashboardPage'));
const EventBuilderPage = React.lazy(() => import('@/features/ptm/EventBuilderPage'));
const FleetMapPage = React.lazy(() => import('@/features/transport/FleetMapPage'));
const RouteAssignmentsPage = React.lazy(() => import('@/features/transport/assignments/RouteAssignmentsPage'));
const FleetPage = React.lazy(() => import('@/features/transport/management/FleetPage'));
const StopsPage = React.lazy(() => import('@/features/transport/management/StopsPage'));
const RoutesPage = React.lazy(() => import('@/features/transport/management/RoutesPage'));
const RouteDetailPage = React.lazy(() => import('@/features/transport/management/RouteDetailPage'));
const StudentLeavePage = React.lazy(() => import('@/features/student-leave/StudentLeavePage'));
const GradingConfigPage = React.lazy(() => import('@/features/exams/GradingConfigPage'));
const ExamBuilderPage = React.lazy(() => import('@/features/exams/ExamBuilderPage'));
const MarksGridPage = React.lazy(() => import('@/features/exams/MarksGridPage'));
const ResultsConsolePage = React.lazy(() => import('@/features/exams/ResultsConsolePage'));
const FeesLanding = React.lazy(() => import('@/features/fees/FeesLanding'));
const FeeStructuresPage = React.lazy(() => import('@/features/fees/FeeStructuresPage'));
const FeeHeadsPage = React.lazy(() => import('@/features/fees/FeeHeadsPage'));
const DiscountsCatalogPage = React.lazy(() => import('@/features/fees/DiscountsCatalogPage'));
const ConcessionQueuePage = React.lazy(() => import('@/features/fees/ConcessionQueuePage'));
const DefaultersPage = React.lazy(() => import('@/features/fees/DefaultersPage'));
const FeeSummaryPage = React.lazy(() => import('@/features/fees/FeeSummaryPage'));
const CollectionDeskPage = React.lazy(() => import('@/features/fees/CollectionDeskPage'));
const ChequeReconciliationPage = React.lazy(() => import('@/features/fees/ChequeReconciliationPage'));
const BankAccountsPage = React.lazy(() => import('@/features/fees/BankAccountsPage'));
const LateFeeRulePage = React.lazy(() => import('@/features/fees/LateFeeRulePage'));
const CatalogPage = React.lazy(() => import('@/features/inventory/CatalogPage'));
const RequisitionsPage = React.lazy(() => import('@/features/inventory/RequisitionsPage'));
const ProcurementPage = React.lazy(() => import('@/features/inventory/ProcurementPage'));
const AuditsPage = React.lazy(() => import('@/features/inventory/AuditsPage'));
const AssetsPage = React.lazy(() => import('@/features/inventory/AssetsPage'));
const LibraryCatalogPage = React.lazy(() => import('@/features/library/CatalogPage'));
const LibraryCirculationPage = React.lazy(() => import('@/features/library/CirculationPage'));
const LibraryRenewalsPage = React.lazy(() => import('@/features/library/RenewalsPage'));
const LibraryRequisitionsPage = React.lazy(() => import('@/features/library/RequisitionsPage'));
const LibraryPoliciesPage = React.lazy(() => import('@/features/library/PoliciesPage'));
const GateVisitorsPage = React.lazy(() => import('@/features/gate/VisitorsPage'));
const GateEarlyCheckoutPage = React.lazy(() => import('@/features/gate/EarlyCheckoutPage'));
const GatePickupLogPage = React.lazy(() => import('@/features/gate/PickupLogPage'));
const AdmissionsPipelinePage = React.lazy(() => import('@/features/admissions/PipelinePage'));
const AdmissionsWaitlistPage = React.lazy(() => import('@/features/admissions/WaitlistPage'));
const AdmissionsInquiryDetailPage = React.lazy(() => import('@/features/admissions/InquiryDetailPage'));
const LessonPlansMinePage = React.lazy(() => import('@/features/lesson-plans/MyPlansPage'));
const LessonPlansReviewPage = React.lazy(() => import('@/features/lesson-plans/ReviewQueuePage'));
const HomeworkPage = React.lazy(() => import('@/features/homework/HomeworkPage'));
const DiaryPage = React.lazy(() => import('@/features/diary/DiaryPage'));
const AnalyticsDashboardPage = React.lazy(() => import('@/features/analytics/DashboardPage'));
const ExpensesLanding = React.lazy(() => import('@/features/expenses/ExpensesLanding'));
const ExpensesClaimsPage = React.lazy(() => import('@/features/expenses/ClaimsPage'));
const ExpensesBudgetsPage = React.lazy(() => import('@/features/expenses/BudgetsPage'));
const ExpensesReportsPage = React.lazy(() => import('@/features/expenses/ReportsPage'));
// HR / Payroll console (docs/design/hr-payroll-web-lld.md) — org-level,
// Go-backed, no SchoolGate (every /hr and /payroll route carries no schoolID).
const HrStaffDirectoryPage = React.lazy(() => import('@/features/hr/StaffDirectoryPage'));
const HrStaffProfilePage = React.lazy(() => import('@/features/hr/StaffProfilePage'));
const HrSalaryStructuresPage = React.lazy(() => import('@/features/hr/SalaryStructuresPage'));
const HrPayrollRunsPage = React.lazy(() => import('@/features/hr/PayrollRunsPage'));
const HrPayslipsPage = React.lazy(() => import('@/features/hr/PayslipsPage'));
const HrExitLettersPage = React.lazy(() => import('@/features/hr/ExitLettersPage'));
const AttendanceGeoConfigPage = React.lazy(() => import('@/features/attendance/GeoConfigPage'));
const AttendanceReviewPage = React.lazy(() => import('@/features/attendance/AttendanceReviewPage'));
// Staff regularization (docs/design/phase-a-web-surfaces-lld.md §3) — a
// self-service request list + a manager approval queue that embeds the shared
// approvals inbox filtered to request_type=regularization.
const RegularizationsPage = React.lazy(() => import('@/features/attendance/RegularizationsPage'));
const RegularizationApprovalsPage = React.lazy(() => import('@/features/attendance/RegularizationApprovalsPage'));
// Student attendance web view (phase-a-web-surfaces-lld.md §4) — view-only
// (marking stays app-only): a school-scoped section register + a per-student
// month calendar. Both gate on attendance.view_school.
const SectionRegisterPage = React.lazy(() => import('@/features/attendance/SectionRegisterPage'));
const StudentMonthPage = React.lazy(() => import('@/features/attendance/StudentMonthPage'));
// Attendance dashboard (phase-c-consolidation-lld.md C1) — composes the at-risk
// <75% list with today's attendance context. Gate: attendance.view_school.
const AttendanceDashboardPage = React.lazy(() => import('@/features/attendance/AttendanceDashboardPage'));
// Per-staff "My Timetable" read-only viewer (phase-c C2) over self-scoped
// GET /staff/me/timetable — any authenticated staff, no extra gate.
const MyTimetablePage = React.lazy(() => import('@/features/my-timetable/MyTimetablePage'));
// Leave Management console (phase-a-web-surfaces-lld.md §2) — org-level,
// Go-backed, no SchoolGate. Supersedes the dead legacy /leaves (Fastify).
const LeaveRequestsPage = React.lazy(() => import('@/features/leave/LeaveRequestsPage'));
const LeaveRequestDetailPage = React.lazy(() => import('@/features/leave/LeaveRequestDetailPage'));
const LeaveBalancesPage = React.lazy(() => import('@/features/leave/LeaveBalancesPage'));
const LeaveTypesPage = React.lazy(() => import('@/features/leave/LeaveTypesPage'));
const LeaveApprovalsPage = React.lazy(() => import('@/features/leave/LeaveApprovalsPage'));
// Shared Approvals console (phase-a-web-surfaces-lld.md §1) — the generic
// approval-engine inbox + self-service delegations, staff-scoped (no
// usePermission; the inbox self-scopes by reporting chain / delegation).
const ApprovalsInboxPage = React.lazy(() => import('@/features/approvals/ApprovalsInboxPage'));
// User/Role admin console (phase-a-web-surfaces-lld.md §2d/§6) — over the RBAC
// backend (/admin/*), org-level, gated users.manage. Supersedes the dead
// legacy /admin/users. Static sub-pages rank above the dynamic "users/:id".
const AdminUsersPage = React.lazy(() => import('@/features/admin/UsersPage'));
const AdminUserDetailPage = React.lazy(() => import('@/features/admin/UserDetailPage'));
const AdminRolesPage = React.lazy(() => import('@/features/admin/RolesPage'));
const AdminReportingPage = React.lazy(() => import('@/features/admin/ReportingPage'));
// Team / Org chart (completeness-lld.md Slice 1) — visual reporting-tree over
// GET /admin/org-tree, colored by wing. Same admin visibility as the console.
const TeamHierarchyPage = React.lazy(() => import('@/features/admin/TeamHierarchyPage'));
const CalendarPage = React.lazy(() => import('@/features/calendar/CalendarPage'));
// Communication admin console (Module 15, gap #33,
// docs/design/communication-admin-lld.md) — school-scoped announcements
// composer. Sibling to the already-shipped calendar console above.
const AnnouncementsPage = React.lazy(() => import('@/features/communication/AnnouncementsPage'));
// Gallery console (gap communication-gallery-no-ui) — school-scoped album
// grid + item detail drawer, sibling to Announcements above.
const GalleryPage = React.lazy(() => import('@/features/gallery/GalleryPage'));
// Transport cost/fee surfaces (gap transport-cost-fee-management) — per-bus
// cost detail (reached via a row link on FleetPage) and the org-wide route
// fee-slab editor (reached via the Transport Setup subnav's "Fees" tab).
const BusCostPage = React.lazy(() => import('@/features/transport/management/cost/BusCostPage'));
const CostSetupPage = React.lazy(() => import('@/features/transport/management/cost-setup/CostSetupPage'));
// Self-service HR profile (gap hr-self-service-me-profile) — any staff, over
// the previously-unreachable GET /me/profile.
const MyProfilePage = React.lazy(() => import('@/features/my-profile/MyProfilePage'));
const LeadsPage = React.lazy(() => import('@/pages/leads/LeadsPage'));
const LeadDetailsPage = React.lazy(() => import('@/pages/leads/LeadDetailsPage'));
const LeadConfigPage = React.lazy(() => import('@/pages/leads/LeadConfigPage'));
const LeadsDashboardPage = React.lazy(() => import('@/pages/dashboard/LeadsDashboardPage'));
const SalesReportPage = React.lazy(() => import('@/pages/leads/SalesReportPage'));
// CRM Milestone 4 (docs/design/crm-m4-lld.md §6) — WATI WhatsApp config +
// chat inbox, Go-backed.
const WatiConfigPage = React.lazy(() => import('@/pages/leads/WatiConfigPage'));
const ChatInboxPage = React.lazy(() => import('@/pages/leads/ChatInboxPage'));
// CRM Milestone 5 (docs/design/crm-m5-lld.md §6) — automation rules, their
// execution audit trail, and scheduled WhatsApp messages, Go-backed.
const RulesPage = React.lazy(() => import('@/pages/leads/RulesPage'));
const RuleExecutionsPage = React.lazy(() => import('@/pages/leads/RuleExecutionsPage'));
const ScheduledMessagesPage = React.lazy(() => import('@/pages/leads/ScheduledMessagesPage'));
// Appointments (phase-c §4) — rewired onto apiV2; the /app home for the CRM
// appointments surface (default export is the ErrorBoundary+SchoolGate wrapper).
const AppointmentsPage = React.lazy(() => import('@/pages/appointments/AppointmentsPage'));
// CRM Tasks (completeness-lld.md Slice 3) — modern board over the Go crm_tasks
// backend (lead-linked follow-ups); replaces the legacy /sales/tasks page.
const CrmTasksPage = React.lazy(() => import('@/pages/leads/CrmTasksPage'));

// ---- Session + page loaders -------------------------------------------------

function ProtectedRouteV2({ children }: { children: React.ReactNode }) {
  const status = useAuthV2Store((s) => s.status);
  const accessToken = useAuthV2Store((s) => s.accessToken);
  const user = useAuthV2Store((s) => s.user);
  const hydrateFromStorage = useAuthV2Store((s) => s.hydrateFromStorage);
  const setMe = useAuthV2Store((s) => s.setMe);
  const acceptDegradedSession = useAuthV2Store((s) => s.acceptDegradedSession);
  const [denial, setDenial] = React.useState<WebAccessDenial | null>(null);

  // ADR-0007: register FCM token via PUT /me/device once authenticated.
  useRegisterWebDevice();

  useEffect(() => {
    if (status === 'idle') {
      hydrateFromStorage();
    }
  }, [status, hydrateFromStorage]);

  // Hold the shell until /me lands. Tokens-only is status 'loading';
  // painting nav against [] permissions is a blank white sidebar.
  useEffect(() => {
    if (!accessToken || user) return;
    if (status !== 'loading' && status !== 'authenticated') return;
    let cancelled = false;
    authApi.me().then((me) => {
      if (cancelled) return;
      const blocked = webAccessDenial(me);
      if (blocked) {
        setDenial(blocked);
        return;
      }
      setMe(me);
    }).catch((err: unknown) => {
      if (cancelled) return;
      if (err instanceof ApiError && err.status === 401) {
        useAuthV2Store.getState().logout();
        return;
      }
      acceptDegradedSession();
    });
    return () => {
      cancelled = true;
    };
  }, [status, accessToken, user, setMe, acceptDegradedSession]);

  const phase = sessionPhase({ status, accessToken, denial });
  if (phase === 'blocked' && denial) return <WebAccessDenied denial={denial} />;
  if (phase === 'boot') return <SessionLoader />;
  if (phase === 'signin') return <Navigate to="/app/login" replace />;
  return <>{children}</>;
}

function WebHomeRedirect() {
  const permissions = useAuthV2Store((s) => s.permissions);
  const { schoolId } = useSelectedSchoolId();
  const visible = navigationItems.filter((item) => navItemVisible(item, permissions, schoolId));
  const preferred = [
    '/app/analytics',
    '/app/students',
    '/app/transport',
    '/app/fees',
    '/app/hr',
    '/app/library',
    '/app/tickets',
    '/app/academic',
  ];
  const href =
    preferred.find((path) => visible.some((item) => item.href === path)) ??
    visible[0]?.href ??
    '/app/my-profile';
  return <Navigate to={href} replace />;
}

// ---- Root AppV2 component ---------------------------------------------------

export function AppV2() {
  return (
    <Routes>
      <Route
        path="login"
        element={
          <Suspense fallback={<SessionLoader />}>
            <LoginV2Page />
          </Suspense>
        }
      />

      <Route
        path="*"
        element={
          <ProtectedRouteV2>
            <AppShellLegacy>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route index element={<WebHomeRedirect />} />
                  <Route path="students" element={<StudentsListPage />} />
                  <Route path="teachers" element={<TeachersPage />} />
                  <Route path="students/:studentId" element={<StudentDetailPage />} />
                  {/* SIS admin console (gap #36) — school-scoped. The static
                      "promotion/history" route ranks above "promotion"
                      regardless of declaration order (RRv6 route ranking), but
                      declaring it first keeps this readable — same precedent as
                      the fees/exams sub-pages below. */}
                  <Route path="sis" element={<Navigate to="/app/sis/roster" replace />} />
                  <Route path="sis/roster" element={<SisRosterPage />} />
                  <Route path="sis/promotion/history" element={<SisPromotionHistoryPage />} />
                  <Route path="sis/promotion" element={<SisPromotionWizard />} />
                  <Route path="academic" element={<AcademicStructurePage />} />
                  {/* Per-staff "My Timetable" viewer (phase-c C2) — any staff, self-scoped. */}
                  <Route path="my-timetable" element={<MyTimetablePage />} />
                  {/* Self-service HR profile (gap hr-self-service-me-profile)
                      — any staff, self-scoped over GET /me/profile, no
                      RBAC gate (actor.IsStaff() only), same precedent as
                      My Timetable above. */}
                  <Route path="my-profile" element={<MyProfilePage />} />
                  {/* Org & School Settings console — org-scoped, no SchoolGate
                      (mirrors HR). Guarded in-page by settings.manage: the route
                      stays mounted and shows the permission banner if lacking. */}
                  <Route path="settings" element={<SettingsConsolePage />} />
                  <Route path="settings/*" element={<Navigate to="/app/settings" replace />} />
                  <Route path="tickets" element={<TicketsPage />} />
                  <Route path="tickets/:id" element={<TicketDetailsPage />} />
                  <Route path="ptm" element={<PtmDashboardPage />} />
                  <Route path="ptm/new" element={<EventBuilderPage />} />
                  <Route path="ptm/:id" element={<PtmDashboardPage />} />
                  {/* Static + dynamic assignment routes rank above the transport/*
                      splat below regardless of declaration order (RRv6 route
                      ranking), but declaring them first keeps this readable. */}
                  <Route path="transport/fleet" element={<FleetPage />} />
                  <Route path="transport/fleet/:busId/cost" element={<BusCostPage />} />
                  <Route path="transport/cost-setup" element={<CostSetupPage />} />
                  <Route path="transport/fee-slabs" element={<Navigate to="/app/transport/cost-setup" replace />} />
                  <Route path="transport/stops" element={<StopsPage />} />
                  <Route path="transport/routes" element={<RoutesPage />} />
                  <Route path="transport/routes/:routeId" element={<RouteDetailPage />} />
                  <Route path="transport/routes/:routeId/assignments" element={<RouteAssignmentsPage />} />
                  <Route path="transport/*" element={<FleetMapPage />} />
                  <Route path="student-leave" element={<StudentLeavePage />} />
                  {/* Static "config" route ranks above the dynamic ":id" route
                      below regardless of declaration order (RRv6 route
                      ranking), but declaring it first keeps this readable —
                      same precedent as the transport routes/assignments block
                      above. */}
                  <Route path="exams/config" element={<GradingConfigPage />} />
                  {/* Static "results" route ranks above the dynamic ":id" route
                      below regardless of declaration order (RRv6 route
                      ranking), but declaring it first keeps this readable —
                      same precedent as "exams/config" above. */}
                  <Route path="exams/results" element={<ResultsConsolePage />} />
                  <Route path="exams/:id/subjects/:subjectId/marks" element={<MarksGridPage />} />
                  <Route path="exams/:id" element={<ExamBuilderPage />} />
                  <Route path="exams" element={<ExamBuilderPage />} />
                  {/* Static fees sub-pages rank above the dynamic ":id" route
                      below regardless of declaration order (RRv6 route
                      ranking), but declaring them first keeps this readable —
                      same precedent as "exams/config" above. */}
                  <Route path="fees/heads" element={<FeeHeadsPage />} />
                  <Route path="fees/discounts" element={<DiscountsCatalogPage />} />
                  <Route path="fees/concessions" element={<ConcessionQueuePage />} />
                  <Route path="fees/collection" element={<CollectionDeskPage />} />
                  <Route path="fees/cheques" element={<ChequeReconciliationPage />} />
                  <Route path="fees/bank-accounts" element={<BankAccountsPage />} />
                  <Route path="fees/late-fee-rule" element={<LateFeeRulePage />} />
                  <Route path="fees/defaulters" element={<DefaultersPage />} />
                  <Route path="fees/summary" element={<FeeSummaryPage />} />
                  <Route path="fees/:id" element={<FeeStructuresPage />} />
                  <Route path="fees" element={<FeesLanding />} />
                  {/* Static inventory sub-pages rank above each page's own
                      dynamic ":id" route below regardless of declaration
                      order (RRv6 route ranking), but declaring them first
                      keeps this readable — same precedent as "exams/config"
                      above. */}
                  <Route path="inventory/requisitions" element={<RequisitionsPage />} />
                  <Route path="inventory/procurement/:id" element={<ProcurementPage />} />
                  <Route path="inventory/procurement" element={<ProcurementPage />} />
                  <Route path="inventory/audits/:id" element={<AuditsPage />} />
                  <Route path="inventory/audits" element={<AuditsPage />} />
                  <Route path="inventory/assets/:id" element={<AssetsPage />} />
                  <Route path="inventory/assets" element={<AssetsPage />} />
                  <Route path="inventory/catalog/:id" element={<CatalogPage />} />
                  <Route path="inventory/catalog" element={<CatalogPage />} />
                  <Route path="inventory" element={<Navigate to="catalog" replace />} />
                  {/* Static library sub-pages rank above the dynamic
                      "catalog/:id" route below regardless of declaration
                      order (RRv6 route ranking), but declaring them first
                      keeps this readable — same precedent as "exams/config"
                      above. */}
                  <Route path="library/circulation" element={<LibraryCirculationPage />} />
                  <Route path="library/renewals" element={<LibraryRenewalsPage />} />
                  <Route path="library/requisitions" element={<LibraryRequisitionsPage />} />
                  <Route path="library/policies" element={<LibraryPoliciesPage />} />
                  <Route path="library/catalog/:id" element={<LibraryCatalogPage />} />
                  <Route path="library/catalog" element={<LibraryCatalogPage />} />
                  <Route path="library" element={<Navigate to="catalog" replace />} />
                  {/* Gate/Visitor — no dynamic ":id" routes, so declaration
                      order doesn't matter here, but the three static pages
                      are listed above the module's index redirect for
                      readability, matching the library/inventory precedent. */}
                  <Route path="gate/visitors" element={<GateVisitorsPage />} />
                  <Route path="gate/early-checkout" element={<GateEarlyCheckoutPage />} />
                  <Route path="gate/pickup-log" element={<GatePickupLogPage />} />
                  <Route path="gate" element={<Navigate to="visitors" replace />} />
                  {/* Admissions — static pipeline/waitlist sub-pages rank above
                      the dynamic ":id" inquiry-detail route below regardless
                      of declaration order (RRv6 route ranking), but declaring
                      them first keeps this readable — same precedent as
                      "exams/config" above. */}
                  <Route path="admissions/pipeline" element={<AdmissionsPipelinePage />} />
                  <Route path="admissions/waitlist" element={<AdmissionsWaitlistPage />} />
                  <Route path="admissions/:id" element={<AdmissionsInquiryDetailPage />} />
                  <Route path="admissions" element={<Navigate to="pipeline" replace />} />
                  {/* Lesson Plans — two static sub-pages, no dynamic ":id"
                      route (row detail expands in place), same precedent as
                      Gate above. */}
                  <Route path="lesson-plans/mine" element={<LessonPlansMinePage />} />
                  <Route path="lesson-plans/review" element={<LessonPlansReviewPage />} />
                  <Route path="lesson-plans" element={<Navigate to="mine" replace />} />
                  {/* Homework & Diary (Module 6) — two sibling single-page
                      consoles, no dynamic ":id" route (row detail/edit
                      expands in place), same precedent as Lesson Plans
                      above. */}
                  <Route path="homework" element={<HomeworkPage />} />
                  <Route path="diary" element={<DiaryPage />} />
                  {/* Analytics — a single composed dashboard page, no sub-nav. */}
                  <Route path="analytics" element={<AnalyticsDashboardPage />} />
                  {/* HR & Payroll (docs/design/hr-payroll-web-lld.md) —
                      org-level, no SchoolGate. Static sub-pages rank above the
                      dynamic ":staffID"/":runID" routes regardless of
                      declaration order (RRv6 route ranking), but declaring
                      them first keeps this readable — same precedent as
                      "exams/config" above. */}
                  <Route path="hr/salary" element={<HrSalaryStructuresPage />} />
                  <Route path="hr/runs/:runID/payslips" element={<HrPayslipsPage />} />
                  <Route path="hr/runs" element={<HrPayrollRunsPage />} />
                  <Route path="hr/exits" element={<HrExitLettersPage />} />
                  <Route path="hr/staff/:staffID" element={<HrStaffProfilePage />} />
                  <Route path="hr" element={<HrStaffDirectoryPage />} />
                  {/* Expenses — "expenses" itself is the record/register
                      sub-page (mirrors gate/admissions's default sub-page),
                      no redirect needed since it isn't a bare index. */}
                  <Route path="expenses" element={<ExpensesLanding />} />
                  <Route path="expenses/claims" element={<ExpensesClaimsPage />} />
                  <Route path="expenses/budgets" element={<ExpensesBudgetsPage />} />
                  <Route path="expenses/reports" element={<ExpensesReportsPage />} />
                  {/* Staff Attendance selfie+geofence (docs/design/
                      staff-attendance-selfie-geofence-lld.md) — two static
                      sub-pages, no dynamic ":id" route, same precedent as
                      Gate/Lesson-Plans above. */}
                  <Route path="attendance/geo-config" element={<AttendanceGeoConfigPage />} />
                  <Route path="attendance/review" element={<AttendanceReviewPage />} />
                  {/* Staff regularization (phase-a §3) — request list + manager
                      approval queue. Static, no dynamic ":id" (there is no
                      regularization by-id backend route), so declaration order
                      is only for readability. */}
                  <Route path="attendance/regularizations/approvals" element={<RegularizationApprovalsPage />} />
                  <Route path="attendance/regularizations" element={<RegularizationsPage />} />
                  {/* Student attendance web view (phase-a §4) — view-only
                      section register + per-student month calendar. The static
                      "register" route ranks above the dynamic "student/:id"
                      regardless of declaration order (RRv6 route ranking), but
                      declaring it first keeps this readable. */}
                  <Route path="attendance/dashboard" element={<AttendanceDashboardPage />} />
                  <Route path="attendance/register" element={<SectionRegisterPage />} />
                  <Route path="attendance/student/:enrollmentID" element={<StudentMonthPage />} />
                  <Route path="attendance" element={<Navigate to="register" replace />} />
                  {/* Leave Management console (phase-a §2) — org-level, no
                      SchoolGate. Static sub-pages rank above the dynamic
                      "requests/:id" route regardless of declaration order (RRv6
                      route ranking), but declaring them first keeps this
                      readable — same precedent as "exams/config" above. */}
                  <Route path="leave/balances" element={<LeaveBalancesPage />} />
                  <Route path="leave/types" element={<LeaveTypesPage />} />
                  <Route path="leave/approvals" element={<LeaveApprovalsPage />} />
                  <Route path="leave/requests/:id" element={<LeaveRequestDetailPage />} />
                  <Route path="leave" element={<LeaveRequestsPage />} />
                  {/* Shared Approvals console (phase-a §1) — cross-type inbox +
                      delegations. Static "delegations" sub-page above the index. */}
                  <Route path="approvals/delegations" element={<ApprovalsInboxPage tab="delegations" />} />
                  <Route path="approvals" element={<ApprovalsInboxPage tab="pending" />} />
                  {/* User/Role admin console (phase-a §2d/§6) — org-level, gated
                      users.manage. Static sub-pages rank above the dynamic
                      "users/:id" detail route regardless of declaration order
                      (RRv6 route ranking), but declaring them first keeps this
                      readable — same precedent as "exams/config" above. */}
                  <Route path="admin/users" element={<AdminUsersPage />} />
                  <Route path="admin/roles" element={<AdminRolesPage />} />
                  <Route path="admin/reporting" element={<AdminReportingPage />} />
                  <Route path="team" element={<TeamHierarchyPage />} />
                  <Route path="admin/users/:id" element={<AdminUserDetailPage />} />
                  <Route path="admin" element={<Navigate to="admin/users" replace />} />
                  {/* Academic Calendar (Module 15) — a single console page,
                      no sub-nav, same precedent as Analytics above. */}
                  <Route path="calendar" element={<CalendarPage />} />
                  {/* Communication admin console (Module 15, gap #33) — a
                      single school-scoped announcements console, no sub-nav
                      of its own (it cross-links to /app/calendar), same
                      precedent as the calendar page above. */}
                  <Route path="communication" element={<AnnouncementsPage />} />
                  {/* Gallery console (gap communication-gallery-no-ui) —
                      school-scoped album grid + item detail drawer, sibling
                      to Announcements above. */}
                  <Route path="gallery" element={<GalleryPage />} />
                  {/* Whole-school Task Management (Module 10,
                      docs/design/tasks-module-ui-lld.md) — org-scoped, no
                      SchoolGate. The static "tasks" list ranks above the
                      dynamic ":id" detail route below regardless of
                      declaration order (RRv6 route ranking), but declaring
                      it first keeps this readable — same precedent as
                      "exams/config" above. */}
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="tasks/:id" element={<TaskDetailPage />} />
                  {/* CRM/Leads (Go CRM Milestones 1+2) — static sub-pages rank
                      above the dynamic ":id" lead-detail route below
                      regardless of declaration order (RRv6 route ranking),
                      but declaring them first keeps this readable — same
                      precedent as "exams/config" above. */}
                  <Route path="leads/dashboard" element={<LeadsDashboardPage />} />
                  <Route path="leads/reports" element={<SalesReportPage />} />
                  <Route path="leads/config" element={<LeadConfigPage />} />
                  {/* CRM Milestone 4 (docs/design/crm-m4-lld.md §6) — WATI
                      config + chat inbox, static siblings alongside the
                      other leads/* sub-pages above. */}
                  <Route path="leads/wati" element={<WatiConfigPage />} />
                  <Route path="leads/inbox" element={<ChatInboxPage />} />
                  {/* CRM Milestone 5 (docs/design/crm-m5-lld.md §6) — automation
                      rules, rule execution audit, scheduled WhatsApp messages,
                      static siblings alongside the other leads/* sub-pages
                      above (same static-before-dynamic precedent). */}
                  <Route path="leads/rules" element={<RulesPage />} />
                  <Route path="leads/rule-executions" element={<RuleExecutionsPage />} />
                  <Route path="leads/scheduled-messages" element={<ScheduledMessagesPage />} />
                  <Route path="leads/appointments" element={<AppointmentsPage />} />
                  <Route path="leads/tasks" element={<CrmTasksPage />} />
                  <Route path="leads/:id" element={<LeadDetailsPage />} />
                  <Route path="leads" element={<LeadsPage />} />
                  <Route path="*" element={<Navigate to="students" replace />} />
                </Routes>
              </Suspense>
            </AppShellLegacy>
          </ProtectedRouteV2>
        }
      />
    </Routes>
  );
}
